import { useState, useEffect, useRef } from 'react';
import { Shield, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { useTwoFactorAuthStore } from '@/store/twoFactorAuth';
import { useOwner2FAApproval } from '@/hooks/useOwner2FAApproval';

const ACTION_LABELS: Record<string, string> = {
  void_order: 'Void Order',
  refund: 'Process Refund',
  cash_adjustment: 'Cash Adjustment',
  discount: 'Apply Discount',
  role_change: 'Change User Role',
  user_deactivate: 'Deactivate User',
  primary_admin_change: 'Change Business Owner',
  disable_2fa: 'Disable 2FA',
  security_settings: 'Security Settings',
  export_audit_logs: 'Export Audit Logs',
};

export function OwnerTwoFactorDialog() {
  const { pendingOwnerApproval, isOwnerApprovalDialogOpen, setOwnerApprovalDialogOpen } =
    useTwoFactorAuthStore();
  const { verifyAndApprove, cancelApproval } = useOwner2FAApproval();

  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(60);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when dialog opens
  useEffect(() => {
    if (isOwnerApprovalDialogOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setCode('');
      setError(null);
      setTimeRemaining(60);
    }
  }, [isOwnerApprovalDialogOpen]);

  // Countdown timer
  useEffect(() => {
    if (!isOwnerApprovalDialogOpen) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOwnerApprovalDialogOpen]);

  // Auto-submit when 6 digits entered
  useEffect(() => {
    if (code.length === 6) {
      handleVerify();
    }
  }, [code]);

  const handleTimeout = () => {
    cancelApproval();
    setOwnerApprovalDialogOpen(false);
  };

  const handleVerify = async () => {
    if (code.length !== 6 || isVerifying) return;

    setIsVerifying(true);
    setError(null);

    try {
      const success = await verifyAndApprove(code);
      if (!success) {
        setError('Invalid verification code');
        setCode('');
        inputRef.current?.focus();
      }
    } catch (err) {
      setError('Verification failed');
      setCode('');
      inputRef.current?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCancel = () => {
    cancelApproval();
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    if (digits.length <= 6) {
      setCode(digits);
      setError(null);
    }
  };

  const actionLabel = pendingOwnerApproval
    ? ACTION_LABELS[pendingOwnerApproval.actionType] || pendingOwnerApproval.actionType
    : '';

  return (
    <ResponsiveModal open={isOwnerApprovalDialogOpen} onOpenChange={setOwnerApprovalDialogOpen}>
      <ResponsiveModalContent className="max-w-md">
        <ResponsiveModalHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <ResponsiveModalTitle>Owner Approval Required</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                {actionLabel}
              </ResponsiveModalDescription>
            </div>
          </div>
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="space-y-4">
          {/* Action Description */}
          {pendingOwnerApproval && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium">{pendingOwnerApproval.description}</p>
            </div>
          )}

          {/* Warning */}
          <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
            <p className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              This action requires owner verification. Enter your 2FA code to approve.
            </p>
          </div>

          {/* Code Input */}
          <div className="space-y-2">
            <Label htmlFor="owner-2fa-code">Verification Code</Label>
            <Input
              ref={inputRef}
              id="owner-2fa-code"
              type="text"
              inputMode="numeric"
              value={code}
              onChange={handleCodeChange}
              placeholder="000000"
              className={`text-center text-2xl tracking-widest font-mono ${
                error ? 'border-red-500' : ''
              }`}
              disabled={isVerifying}
              autoComplete="one-time-code"
            />
            {error && <p className="text-sm text-red-500 text-center">{error}</p>}
          </div>

          {/* Timer */}
          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              Time remaining: <span className={timeRemaining <= 10 ? 'text-red-500 font-medium' : ''}>
                {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
              </span>
            </p>
          </div>
        </ResponsiveModalBody>

        <ResponsiveModalFooter className="flex gap-3">
          <Button variant="outline" onClick={handleCancel} className="flex-1" disabled={isVerifying}>
            Cancel
          </Button>
          <Button
            onClick={handleVerify}
            disabled={code.length !== 6 || isVerifying}
            className="flex-1"
          >
            {isVerifying ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              'Approve'
            )}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
